<!-- Loading State -->
<div *ngIf="planLoading" class="min-h-screen flex items-center justify-center bg-gray-50">
  <div class="text-center">
    <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto"></div>
    <p class="text-gray-600 mt-4 text-lg">Cargando detalles del plan...</p>
    <p class="text-gray-400 mt-2 text-sm">Esto puede tomar unos segundos</p>
  </div>
</div>

<!-- Main Content -->
<div *ngIf="!planLoading && plan" class="min-h-screen bg-gray-50">

  <!-- Header with Breadcrumb and Actions -->
  <div class="bg-white shadow-sm border-b">
    <div class="w-full px-6 py-6">
      <div>
        <!-- Breadcrumb -->
        <nav class="flex items-center space-x-2 text-sm text-gray-500 mb-4">
          <button (click)="goBack()" class="hover:text-blue-600 transition-colors flex items-center">
            <i class="fas fa-arrow-left mr-2"></i>
            Dashboard de Planes
          </button>
          <i class="fas fa-chevron-right text-xs"></i>
          <span class="text-gray-900 font-medium">{{ plan.code }}</span>
        </nav>

        <!-- Header Info -->
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center space-x-3 mb-2">
              <h1 class="text-2xl font-bold text-gray-900">{{ plan.name }}</h1>
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    [ngClass]="getStatusBadgeClass(plan.status)"
                    [title]="plan.status ? 'Plan activo y disponible para clientes' : 'Plan inactivo, no visible para clientes'">
                <i class="fas fa-circle text-xs mr-1"></i>
                {{ plan.status ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
            <p class="text-gray-600 max-w-2xl" [innerHTML]="plan.description"></p>

            <!-- Quick Info -->
            <div class="flex items-center space-x-6 mt-4 text-sm text-gray-500">
              <div class="flex items-center" title="Código único del plan">
                <i class="fas fa-code mr-2"></i>
                <span>{{ plan.code }}</span>
              </div>
              <!-- OCULTAR VISIBILIDAD POR SOLICITUD DEL USUARIO
              <div class="flex items-center" title="Visibilidad del plan">
                <i class="fas fa-eye mr-2"></i>
                <span>{{ getVisibilityText(plan.visibility) }}</span>
              </div>
              -->
              <div class="flex items-center" title="Fecha de creación">
                <i class="fas fa-calendar mr-2"></i>
                <span>Creado: {{ formatDate(plan.date) }}</span>
              </div>
              <div class="flex items-center" title="Versión actual">
                <i class="fas fa-code-branch mr-2"></i>
                <span>{{ plan.version?.internal_code || 'v1.0' }}</span>
              </div>
              <div class="flex items-center" title="Número de créditos asociados">
                <i class="fas fa-file-invoice mr-2"></i>
                <span>{{ plan.invoices || 0 }} créditos</span>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center space-x-3">
            <button (click)="editPlan()"
                    title="Editar configuración del plan"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
              <i class="fas fa-edit mr-2"></i>
              Editar
            </button>
            <!--
            <button (click)="duplicatePlan()"
                    title="Crear una copia de este plan"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
              <i class="fas fa-copy mr-2"></i>
              Duplicar
            </button>
            -->
            <button (click)="togglePlanStatus()"
                    [title]="plan.status ? 'Desactivar plan (no será visible para clientes)' : 'Activar plan (será visible para clientes)'"
                    class="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                    [class.bg-green-600]="!plan.status"
                    [class.hover:bg-green-700]="!plan.status"
                    [class.focus:ring-green-500]="!plan.status"
                    [class.text-white]="!plan.status"
                    [class.bg-yellow-600]="plan.status"
                    [class.hover:bg-yellow-700]="plan.status"
                    [class.focus:ring-yellow-500]="plan.status"
                    [class.text-white]="plan.status">
              <i class="fas mr-2"
                 [class.fa-play]="!plan.status"
                 [class.fa-pause]="plan.status"></i>
              {{ plan.status ? 'Desactivar' : 'Activar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Layout: 3 Columns (Sidebar + Content + Help) -->
  <div class="w-full px-6 py-8">
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-8">

      <!-- Left Sidebar: Navigation -->
      <div class="xl:col-span-3">
        <div class="bg-white rounded-lg shadow-sm border sticky top-8">
          <div class="p-4 border-b">
            <h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">Navegación</h3>
            <p class="text-xs text-gray-500 mt-1">Explora las diferentes secciones del plan</p>
          </div>
          <nav class="p-2">
            <button *ngFor="let section of getVisibleSections()"
                    (click)="navigateToSection(section.id)"
                    [title]="section.description"
                    class="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors mb-1 group"
                    [class.bg-blue-50]="activeSection === section.id"
                    [class.text-blue-700]="activeSection === section.id"
                    [class.border-r-2]="activeSection === section.id"
                    [class.border-blue-500]="activeSection === section.id"
                    [class.text-gray-600]="activeSection !== section.id"
                    [class.hover:bg-gray-50]="activeSection !== section.id">
              <span class="mr-3 text-sm">{{ section.icon }}</span>
              <span class="flex-1 text-left">{{ section.name }}</span>
              <i class="fas fa-chevron-right text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                 [class.opacity-100]="activeSection === section.id"></i>
            </button>
          </nav>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="xl:col-span-6 space-y-8">

        <!-- Overview Metrics - Cintillo horizontal simple -->
        <!-- <div class="bg-white rounded-lg shadow-sm border p-4 mb-8">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-medium text-gray-600">Resumen General</h3>
            <div class="flex items-center space-x-8">
              <div class="flex items-center">
                <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                  <i class="fas fa-credit-card text-blue-600 text-xs"></i>
                </div>
                <span class="text-sm text-gray-600 mr-2">Créditos Activos:</span>
                <span class="text-sm font-bold text-gray-900">{{ metrics.creditosActivos }}</span>
              </div>

              <div class="flex items-center">
                <div class="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                  <i class="fas fa-check-circle text-green-600 text-xs"></i>
                </div>
                <span class="text-sm text-gray-600 mr-2">Créditos Completados:</span>
                <span class="text-sm font-bold text-gray-900">{{ metrics.creditosCompletados }}</span>
              </div>

              <div class="flex items-center">
                <div class="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                  <i class="fas fa-users text-purple-600 text-xs"></i>
                </div>
                <span class="text-sm text-gray-600 mr-2">Clientes Activos:</span>
                <span class="text-sm font-bold text-gray-900">{{ metrics.clientesActivos }}</span>
              </div>
            </div>
          </div>
        </div> -->

        <!-- Resumen Section -->
        <div id="section-resumen" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'resumen'">
          <div class="mb-6">
            <h2 class="text-xl font-semibold text-gray-900 mb-2">📋 Resumen del Plan</h2>
            <p class="text-sm text-gray-500">Información completa del plan</p>
          </div>

          <!-- Etiquetas -->
          <div class="border rounded-lg p-4 mb-6">
            <h3 class="font-semibold text-gray-800 mb-3">Etiquetas</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Modo de Filtro:</span>
                <span class="text-gray-900">
                  {{ plan.params?.filters?.dinamic_code?.person?.type === 'white_list' ? 'Lista Blanca' :
                     plan.params?.filters?.dinamic_code?.person?.type === 'black_list' ? 'Lista Negra' :
                     'Sin filtro configurado' }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Etiquetas Configuradas:</span>
                <span class="text-gray-900">{{ plan.params?.filters?.dinamic_code?.person?.list?.length || 0 }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.filters?.dinamic_code?.person?.list?.length > 0">
                <span class="text-gray-600">Etiquetas:</span>
                <span class="text-gray-900">
                  <span *ngFor="let tag of plan.params.filters.dinamic_code.person.list.slice(0, 3); let last = last">
                    {{ tag }}<span *ngIf="!last">, </span>
                  </span>
                  <span *ngIf="plan.params.filters.dinamic_code.person.list.length > 3">
                    (+{{ plan.params.filters.dinamic_code.person.list.length - 3 }} más)
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Penalidades -->
          <div class="border rounded-lg p-4 mb-6">
            <h3 class="font-semibold text-gray-800 mb-3">Penalidades (Fees)</h3>
            <div class="space-y-3">
              <!-- Lista Blanca -->
              <div>
                <div class="flex items-center mb-2">
                  <i class="fas fa-check-circle text-green-600 mr-2"></i>
                  <span class="text-sm font-medium text-gray-700">Lista Blanca:</span>
                  <span class="ml-2 text-sm text-gray-600">
                    {{ getWhiteListFees().length || 0 }} 
                    {{ getWhiteListFees().length === 1 ? 'penalidad' : 'penalidades' }}
                  </span>
                </div>
                <div *ngIf="getWhiteListFees().length > 0; else noWhiteListFees" class="ml-6 space-y-1">
                  <div *ngFor="let feeCode of getWhiteListFees().slice(0, 3)" class="text-sm">
                    <span class="font-medium text-green-700">{{ feeCode }}</span>
                    <span class="text-gray-600"> - {{ getFeeName(feeCode) }}</span>
                  </div>
                  <div *ngIf="getWhiteListFees().length > 3" class="text-xs text-green-600 italic">
                    +{{ getWhiteListFees().length - 3 }} más
                  </div>
                </div>
                <ng-template #noWhiteListFees>
                  <div class="ml-6 text-xs text-gray-500 italic">
                    No hay penalidades en lista blanca
                  </div>
                </ng-template>
              </div>

              <!-- Lista Negra -->
              <div>
                <div class="flex items-center mb-2">
                  <i class="fas fa-ban text-red-600 mr-2"></i>
                  <span class="text-sm font-medium text-gray-700">Lista Negra:</span>
                  <span class="ml-2 text-sm text-gray-600">
                    {{ getBlackListFees().length || 0 }} 
                    {{ getBlackListFees().length === 1 ? 'penalidad' : 'penalidades' }}
                  </span>
                </div>
                <div *ngIf="getBlackListFees().length > 0; else noBlackListFees" class="ml-6 space-y-1">
                  <div *ngFor="let feeCode of getBlackListFees().slice(0, 3)" class="text-sm">
                    <span class="font-medium text-red-700">{{ feeCode }}</span>
                    <span class="text-gray-600"> - {{ getFeeName(feeCode) }}</span>
                  </div>
                  <div *ngIf="getBlackListFees().length > 3" class="text-xs text-red-600 italic">
                    +{{ getBlackListFees().length - 3 }} más
                  </div>
                </div>
                <ng-template #noBlackListFees>
                  <div class="ml-6 text-xs text-gray-500 italic">
                    No hay penalidades en lista negra
                  </div>
                </ng-template>
              </div>
            </div>
          </div>

          <!-- Productos -->
          <div class="border rounded-lg p-4 mb-6">
            <h3 class="font-semibold text-gray-800 mb-3">Productos</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Identificador:</span>
                <span class="text-gray-900 font-mono">{{ plan.params?.product_type || 'N/A' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Producto:</span>
                <span class="text-gray-900">{{ parentProductName || 'Cargando...' }}</span>
              </div>
            </div>
          </div>

          <!-- Datos Básicos -->
          <div class="border rounded-lg p-4 mb-6">
            <h3 class="font-semibold text-gray-800 mb-3">Datos Básicos</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Tipo:</span>
                <span class="text-gray-900">{{ getPlanTypeText() }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Estado del Plan:</span>
                <span class="text-gray-900">{{ (plan.status === true || plan.status === 'ACTIVE') ? 'Activo' : 'Inactivo' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Nombre:</span>
                <span class="text-gray-900">{{ plan.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Código:</span>
                <span class="text-gray-900 font-mono">{{ plan.code }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Multicompra:</span>
                <span class="text-gray-900">{{ plan.params?.multiple_invoices?.status ? 'Sí' : 'No' }}
                  <span *ngIf="plan.params?.multiple_invoices?.status && plan.params?.multiple_invoices?.count > 0" class="text-gray-400">
                    ({{ plan.params?.multiple_invoices?.count }})
                  </span>
                </span>
              </div>
              <!-- OCULTAR VISIBILIDAD POR SOLICITUD DEL USUARIO
              <div class="flex justify-between">
                <span class="text-gray-600">Visibilidad:</span>
                <span class="text-gray-900">{{ getVisibilityText(plan.visibility) }}</span>
              </div>
              -->
              <div class="flex justify-between">
                <span class="text-gray-600">Versión:</span>
                <span class="text-gray-900 font-mono">{{ plan.version?.internal_code || 'v1.0' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Prioridad:</span>
                <span class="text-gray-900">{{ plan.params?.priority || 0 }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Creado:</span>
                <span class="text-gray-900">{{ formatDate(plan.date) }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.init_date || plan.params?.end_date">
                <span class="text-gray-600">Vigencia:</span>
                <span class="text-gray-900">
                  <span *ngIf="plan.params?.init_date">{{ formatDate(plan.params.init_date) }}</span>
                  <span *ngIf="plan.params?.init_date && plan.params?.end_date"> - </span>
                  <span *ngIf="plan.params?.end_date">{{ formatDate(plan.params.end_date) }}</span>
                  <span *ngIf="!plan.params?.end_date && plan.params?.init_date"> - Sin límite</span>
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.description">
                <span class="text-gray-600">Descripción:</span>
                <span class="text-gray-900" [innerHTML]="plan.description"></span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.icon">
                <span class="text-gray-600">Icono:</span>
                <span class="text-gray-900 font-mono">{{ plan.params.icon }}</span>
              </div>
            </div>
          </div>

          <!-- Datos Generales (solo para crédito) -->
          <div class="border rounded-lg p-4 mb-6" *ngIf="isPlanCredit()">
            <h3 class="font-semibold text-gray-800 mb-3">Datos Generales</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Requiere Inicial:</span>
                <span class="text-gray-900">{{ plan.initial?.status ? 'Sí' : 'No' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.initial?.status">
                <span class="text-gray-600">Forma de Cálculo:</span>
                <span class="text-gray-900">{{ plan.initial?.type === 'percentage' ? 'Porcentaje' : 'Monto Absoluto' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.initial?.status">
                <span class="text-gray-600">Valor Inicial:</span>
                <span class="text-gray-900">{{ plan.initial?.amount }}{{ plan.initial?.type === 'percentage' ? '%' : ' USD' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.min_amount">
                <span class="text-gray-600">Monto Mínimo de Compra:</span>
                <span class="text-gray-900">${{ plan.params?.min_amount }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Recalcular Cuotas:</span>
                <span class="text-gray-900">{{ plan.initial?.recalculate ? 'Sí' : 'No' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.payment_terms?.length > 0">
                <span class="text-gray-600">Opciones de Cuotas:</span>
                <span class="text-gray-900">
                  <span *ngFor="let term of plan.payment_terms; let last = last">
                    {{ term.installments }}<span *ngIf="!last">, </span>
                  </span>
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.payment_terms?.[0]?.frequency?.type">
                <span class="text-gray-600">Frecuencia:</span>
                <span class="text-gray-900">
                  {{ plan.payment_terms?.[0]?.frequency?.type === 'mes' ? 'Mensual' :
                     plan.payment_terms?.[0]?.frequency?.type === 'quincenal' ? 'Quincenal' :
                     plan.payment_terms?.[0]?.frequency?.type === 'semanal' ? 'Semanal' :
                     plan.payment_terms?.[0]?.frequency?.type === 'semana' ? 'Semanal' :
                     'Días continuos' }}
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.current_quota_min">
                <span class="text-gray-600">Cupo Mínimo:</span>
                <span class="text-gray-900">${{ plan.params?.current_quota_min }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.current_quota_max">
                <span class="text-gray-600">Cupo Máximo:</span>
                <span class="text-gray-900">${{ plan.params?.current_quota_max }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.min_amount_base">
                <span class="text-gray-600">Monto Mínimo a Financiar:</span>
                <span class="text-gray-900">${{ plan.params?.min_amount_base }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.max_amount_base">
                <span class="text-gray-600">Monto Máximo a Financiar:</span>
                <span class="text-gray-900">${{ plan.params?.max_amount_base }}</span>
              </div>
            </div>
          </div>

          <!-- Filtros -->
          <div class="border rounded-lg p-4 mb-6">
            <h3 class="font-semibold text-gray-800 mb-3">Filtros</h3>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Filtro por Tipo de Persona:</span>
                <span class="text-gray-900">
                  {{ plan.params?.filters?.general?.person?.audience === 'NATURAL' ? 'Persona Natural' :
                     plan.params?.filters?.general?.person?.audience === 'JURIDICA' ? 'Persona Jurídica' :
                     'Sin filtro' }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Filtro por Tiendas:</span>
                <span class="text-gray-900">
                  {{ plan.params?.filters?.general?.invoices?.shop?.type === 'white_list' ? 'Lista Blanca' :
                     plan.params?.filters?.general?.invoices?.shop?.type === 'black_list' ? 'Lista Negra' :
                     'Sin filtro' }}
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.filters?.general?.invoices?.shop?.list?.length > 0">
                <span class="text-gray-600">Tiendas Configuradas:</span>
                <span class="text-gray-900">{{ plan.params?.filters?.general?.invoices?.shop?.list?.length }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Filtro por Productos:</span>
                <span class="text-gray-900">
                  {{ plan.params?.filters?.product_filter?.type === 'white_list' ? 'Lista Blanca' :
                     plan.params?.filters?.product_filter?.type === 'black_list' ? 'Lista Negra' :
                     'Sin filtro' }}
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.filters?.product_filter?.list?.length > 0">
                <span class="text-gray-600">Productos Configurados:</span>
                <span class="text-gray-900">{{ plan.params?.filters?.product_filter?.list?.length }}</span>
              </div>
              <div *ngIf="plan.params?.filters?.product_filter?.list?.length > 0">
                <span class="text-gray-600">Códigos:</span>
                <div class="flex flex-wrap gap-1 mt-1">
                  <span *ngFor="let code of plan.params.filters.product_filter.list"
                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                    <i class="fas fa-box text-xs mr-1"></i>{{ code }}
                  </span>
                </div>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.filters?.product_filter?.type">
                <span class="text-gray-600">Modo de Coincidencia:</span>
                <span class="text-gray-900">
                  {{ (plan.params?.filters?.product_filter?.match_mode === 'all' || plan.params?.filters?.product_filter?.strict_match === true)
                     ? 'Todos deben coincidir'
                     : 'Al menos uno' }}
                </span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.filters?.product_filter?.type === 'white_list'">
                <span class="text-gray-600">Permite otros productos:</span>
                <span class="text-gray-900">
                  {{ plan.params?.filters?.product_filter?.allow_others !== false ? 'Sí' : 'No' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Otros -->
          <div class="border rounded-lg p-4">
            <h3 class="font-semibold text-gray-800 mb-3">Otros</h3>
            <div class="space-y-2">
              <div class="flex justify-between" *ngIf="plan.params?.generate_pagares !== undefined">
                <span class="text-gray-600">Pagaré electrónico:</span>
                <span class="text-gray-900">{{ plan.params?.generate_pagares ? 'Sí' : 'No' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.generate_pagares_fisico !== undefined">
                <span class="text-gray-600">Pagaré físico:</span>
                <span class="text-gray-900">{{ plan.params?.generate_pagares_fisico ? 'Sí' : 'No' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.allow_holidays !== undefined">
                <span class="text-gray-600">Diferimiento de cuotas:</span>
                <span class="text-gray-900">{{ plan.params?.allow_holidays ? 'Diferir al día hábil' : 'Permitir en fines de semana' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.auto_approve !== undefined">
                <span class="text-gray-600">Auto-aprobación:</span>
                <span class="text-gray-900">{{ plan.params?.auto_approve ? 'Activada' : 'Desactivada' }}</span>
              </div>
              <div class="flex justify-between" *ngIf="plan.params?.send_notifications !== undefined">
                <span class="text-gray-600">Notificaciones:</span>
                <span class="text-gray-900">{{ plan.params?.send_notifications ? 'Activadas' : 'Desactivadas' }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Etiquetas Section -->
        <div id="section-etiquetas" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'etiquetas'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Configuración de Etiquetas</h2>
              <p class="text-sm text-gray-500 mt-1">Gestiona qué usuarios pueden acceder a este plan según sus etiquetas</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('etiquetas')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['etiquetas']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['etiquetas'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['etiquetas']" class="mb-6 rounded-lg p-4 bg-blue-50 border border-blue-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-blue-800">¿Cómo funcionan las Etiquetas?</h4>
                <div class="text-sm space-y-2 text-blue-800">
                  <p><strong>Propósito:</strong> Las etiquetas permiten segmentar qué clientes pueden acceder a este plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Modos de Filtro:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Lista Blanca:</strong> Solo usuarios CON estas etiquetas pueden acceder</li>
                      <li><strong>Lista Negra:</strong> Solo usuarios SIN estas etiquetas pueden acceder</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Ejemplos de Etiquetas:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><code class="bg-white px-1 rounded">EMPLEADO_PUBLICO</code> - Funcionarios del sector público</li>
                      <li><code class="bg-white px-1 rounded">CLIENTE_PREMIUM</code> - Clientes con historial crediticio excelente</li>
                      <li><code class="bg-white px-1 rounded">MOROSO</code> - Clientes con pagos en mora</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-blue-100">
                    <p class="font-medium text-xs">💡 Tip:</p>
                    <p class="text-xs">Sin etiquetas = Todos los usuarios pueden acceder</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modo de Filtro -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-gray-700 mb-3">Modo de Filtro</h3>
            <div class="flex items-center space-x-4">
              <div class="flex items-center" *ngIf="plan.params?.filters?.dinamic_code?.person?.type === 'white_list'">
                <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span class="text-sm text-gray-900">Lista Blanca</span>
                <span class="text-xs text-gray-500 ml-2">(Solo usuarios CON estas etiquetas pueden acceder)</span>
              </div>
              <div class="flex items-center" *ngIf="plan.params?.filters?.dinamic_code?.person?.type === 'black_list'">
                <div class="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span class="text-sm text-gray-900">Lista Negra</span>
                <span class="text-xs text-gray-500 ml-2">(Solo usuarios SIN estas etiquetas pueden acceder)</span>
              </div>
              <div class="flex items-center" *ngIf="!plan.params?.filters?.dinamic_code?.person?.type">
                <div class="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                <span class="text-sm text-gray-500">Sin filtro por etiquetas configurado</span>
              </div>
            </div>
          </div>

          <!-- Etiquetas Configuradas -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-gray-700 mb-3">Etiquetas Configuradas</h3>
            <div *ngIf="plan.params?.filters?.dinamic_code?.person?.list?.length > 0; else noEtiquetas">
              <div class="flex flex-wrap gap-2 mb-4">
                <span *ngFor="let code of plan.params.filters.dinamic_code.person.list"
                      class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border">
                  <i class="fas fa-tag text-xs mr-1"></i>
                  {{ code }}
                </span>
              </div>
              <p class="text-sm text-gray-600">
                <span class="font-medium">{{ plan.params.filters.dinamic_code.person.list.length }}</span>
                {{ plan.params.filters.dinamic_code.person.list.length === 1 ? 'etiqueta configurada' : 'etiquetas configuradas' }}
              </p>
            </div>
            <ng-template #noEtiquetas>
              <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-tags text-gray-400 text-2xl mb-2"></i>
                <p class="text-sm text-gray-500">No hay etiquetas configuradas para este plan</p>
                <p class="text-xs text-gray-400 mt-1">Todos los usuarios pueden acceder independientemente de sus etiquetas</p>
              </div>
            </ng-template>
          </div>

          <!-- Impacto de Audiencia -->
          <div class="mb-6" *ngIf="plan.params?.filters?.dinamic_code?.person?.list?.length > 0">
            <h3 class="text-sm font-medium text-gray-700 mb-3">Impacto de Audiencia</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div class="flex items-center">
                  <i class="fas fa-users text-blue-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-blue-900">Usuarios Elegibles</p>
                    <p class="text-lg font-semibold text-blue-800">{{ getEligibleUsersCount() }}</p>
                  </div>
                </div>
              </div>
              <div class="bg-green-50 p-4 rounded-lg border border-green-200">
                <div class="flex items-center">
                  <i class="fas fa-percentage text-green-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-green-900">% de Base Total</p>
                    <p class="text-lg font-semibold text-green-800">{{ getEligibleUsersPercentage() }}%</p>
                  </div>
                </div>
              </div>
              <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div class="flex items-center">
                  <i class="fas fa-chart-pie text-purple-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-purple-900">Alcance</p>
                    <p class="text-lg font-semibold text-purple-800">{{ getReachLevel() }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Acciones -->
          <div class="mt-6 pt-6 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex space-x-3">
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        *ngIf="plan.params?.filters?.dinamic_code?.person?.list?.length > 0">
                  <i class="fas fa-users mr-2"></i>
                  Ver Usuarios Afectados
                </button>
              </div>
              <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      *ngIf="plan.params?.filters?.dinamic_code?.person?.list?.length > 0">
                <i class="fas fa-download mr-2"></i>
                Exportar Lista
              </button>
            </div>
          </div>
        </div>

        <!-- NUEVA SECCIÓN: FILTROS -->
        <div id="section-filtros" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'filtros'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">🔍 Filtros</h2>
              <p class="text-sm text-gray-500 mt-1">Configuración avanzada de elegibilidad del plan</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('filtros')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['filtros']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['filtros'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['filtros']" class="mb-6 rounded-lg p-4 bg-blue-50 border border-blue-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-blue-800">¿Cómo funcionan los Filtros?</h4>
                <div class="text-sm space-y-2 text-blue-800">
                  <p><strong>Propósito:</strong> Los filtros controlan qué usuarios y en qué tiendas está disponible este plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Tipos de Filtros:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Persona:</strong> Filtra por tipo de cliente (Natural/Jurídica)</li>
                      <li><strong>Tiendas:</strong> Controla en qué puntos de venta está disponible</li>
                      <li><strong>Códigos Dinámicos:</strong> Filtros avanzados por etiquetas específicas</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-blue-100">
                    <p class="font-medium text-xs">💡 Tip:</p>
                    <p class="text-xs">Combina múltiples filtros para un control granular de elegibilidad</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Filtro por Tipo de Persona -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-user-tag text-blue-600 mr-2"></i>
              Filtro por Tipo de Persona
            </h3>

            <div *ngIf="plan.params?.filters?.general?.person?.audience; else noPersonFilter">
              <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <i class="fas fa-user-check text-green-600"></i>
                    </div>
                    <div>
                      <h4 class="text-lg font-semibold text-gray-900">
                        {{ plan.params.filters.general.person.audience === 'NATURAL' ? 'Persona Natural' : 'Persona Jurídica' }}
                      </h4>
                      <p class="text-sm text-gray-600">
                        Solo disponible para {{ plan.params.filters.general.person.audience === 'NATURAL' ? 'personas naturales' : 'empresas y organizaciones' }}
                      </p>
                    </div>
                  </div>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i class="fas fa-check-circle mr-1"></i>
                    Activo
                  </span>
                </div>
              </div>
            </div>

            <ng-template #noPersonFilter>
              <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-users text-gray-400 text-2xl mb-2"></i>
                <p class="text-sm text-gray-500">Sin filtro por tipo de persona</p>
                <p class="text-xs text-gray-400 mt-1">Disponible para personas naturales y jurídicas</p>
              </div>
            </ng-template>
          </div>

          <!-- Filtro por Tiendas -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-store text-purple-600 mr-2"></i>
              Filtro por Tiendas
            </h3>

            <div *ngIf="plan.params?.filters?.general?.invoices?.shop; else noShopFilter">
              <!-- Modo de Filtro de Tiendas -->
              <div class="mb-4">
                <div class="flex items-center space-x-4 mb-3">
                  <div class="flex items-center" *ngIf="plan.params.filters.general.invoices.shop.type === 'white_list'">
                    <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span class="text-sm text-gray-900 font-medium">Lista Blanca</span>
                    <span class="text-xs text-gray-500 ml-2">(Solo disponible EN estas tiendas)</span>
                  </div>
                  <div class="flex items-center" *ngIf="plan.params.filters.general.invoices.shop.type === 'black_list'">
                    <div class="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                    <span class="text-sm text-gray-900 font-medium">Lista Negra</span>
                    <span class="text-xs text-gray-500 ml-2">(Disponible EXCEPTO en estas tiendas)</span>
                  </div>
                </div>
              </div>

              <!-- Tiendas Configuradas -->
              <div *ngIf="plan.params.filters.general.invoices.shop.list?.length > 0">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                  <div *ngFor="let shop of plan.params.filters.general.invoices.shop.list"
                       class="bg-white border border-gray-200 rounded-lg p-3 flex items-center space-x-3">
                    <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <i class="fas fa-store text-purple-600 text-sm"></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">{{ shop }}</p>
                      <p class="text-xs text-gray-500">ID: {{ shop }}</p>
                    </div>
                  </div>
                </div>

                <div class="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p class="text-sm text-purple-800">
                    <span class="font-medium">{{ plan.params.filters.general.invoices.shop.list.length }}</span>
                    {{ plan.params.filters.general.invoices.shop.list.length === 1 ? 'tienda configurada' : 'tiendas configuradas' }}
                  </p>
                </div>
              </div>
            </div>

            <ng-template #noShopFilter>
              <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-store-slash text-gray-400 text-2xl mb-2"></i>
                <p class="text-sm text-gray-500">Sin filtro por tiendas</p>
                <p class="text-xs text-gray-400 mt-1">Disponible en todas las tiendas del sistema</p>
              </div>
            </ng-template>
          </div>

          <!-- Códigos Dinámicos Adicionales (Other) -->
          <div class="mb-8" *ngIf="plan.params?.filters?.dinamic_code?.other">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-code text-orange-600 mr-2"></i>
              Códigos Dinámicos Adicionales
            </h3>

            <!-- Modo de Filtro -->
            <div class="mb-4">
              <div class="flex items-center space-x-4 mb-3">
                <div class="flex items-center" *ngIf="plan.params.filters.dinamic_code.other.type === 'white_list'">
                  <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span class="text-sm text-gray-900 font-medium">Lista Blanca</span>
                  <span class="text-xs text-gray-500 ml-2">(Solo usuarios CON estos códigos)</span>
                </div>
                <div class="flex items-center" *ngIf="plan.params.filters.dinamic_code.other.type === 'black_list'">
                  <div class="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  <span class="text-sm text-gray-900 font-medium">Lista Negra</span>
                  <span class="text-xs text-gray-500 ml-2">(Solo usuarios SIN estos códigos)</span>
                </div>
              </div>
            </div>

            <!-- Códigos Configurados -->
            <div *ngIf="plan.params.filters.dinamic_code.other.list?.length > 0; else noOtherCodes">
              <div class="flex flex-wrap gap-2 mb-4">
                <span *ngFor="let code of plan.params.filters.dinamic_code.other.list"
                      class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
                  <i class="fas fa-code text-xs mr-1"></i>
                  {{ code }}
                </span>
              </div>

              <div class="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p class="text-sm text-orange-800">
                  <span class="font-medium">{{ plan.params.filters.dinamic_code.other.list.length }}</span>
                  {{ plan.params.filters.dinamic_code.other.list.length === 1 ? 'código adicional configurado' : 'códigos adicionales configurados' }}
                </p>
              </div>
            </div>

            <ng-template #noOtherCodes>
              <div class="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-code text-gray-400 text-xl mb-2"></i>
                <p class="text-sm text-gray-500">Sin códigos adicionales configurados</p>
              </div>
            </ng-template>
          </div>

          <!-- Resumen de Impacto de Filtros -->
          <div class="mt-8 pt-6 border-t border-gray-200" *ngIf="hasAnyFilters()">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-chart-bar text-indigo-600 mr-2"></i>
              Impacto de Filtros Combinados
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div class="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div class="flex items-center">
                  <i class="fas fa-filter text-indigo-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-indigo-900">Filtros Activos</p>
                    <p class="text-lg font-semibold text-indigo-800">{{ getActiveFiltersCount() }}</p>
                  </div>
                </div>
              </div>
              <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div class="flex items-center">
                  <i class="fas fa-users text-blue-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-blue-900">Usuarios Elegibles</p>
                    <p class="text-lg font-semibold text-blue-800">{{ getFilteredEligibleUsersCount() }}</p>
                  </div>
                </div>
              </div>
              <div class="bg-green-50 p-4 rounded-lg border border-green-200">
                <div class="flex items-center">
                  <i class="fas fa-store text-green-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-green-900">Tiendas Disponibles</p>
                    <p class="text-lg font-semibold text-green-800">{{ getAvailableStoresCount() }}</p>
                  </div>
                </div>
              </div>
              <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div class="flex items-center">
                  <i class="fas fa-percentage text-purple-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-purple-900">Alcance Total</p>
                    <p class="text-lg font-semibold text-purple-800">{{ getFilteredReachLevel() }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Acciones -->
          <div class="mt-6 pt-6 border-t border-gray-200" *ngIf="hasAnyFilters()">
            <div class="flex items-center justify-between">
              <div class="flex space-x-3">
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <i class="fas fa-users mr-2"></i>
                  Simular Audiencia
                </button>
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <i class="fas fa-store mr-2"></i>
                  Ver Tiendas Afectadas
                </button>
              </div>
              <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <i class="fas fa-download mr-2"></i>
                Exportar Configuración
              </button>
            </div>
          </div>
        </div>

        <!-- Penalidades Section -->
        <div id="section-penalidades" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'penalidades'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Configuración de Penalidades (Fees)</h2>
              <p class="text-sm text-gray-500 mt-1">Gestiona las penalidades que se aplicarán en este plan</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('penalidades')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['penalidades']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['penalidades'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['penalidades']" class="mb-6 rounded-lg p-4 bg-blue-50 border border-blue-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-blue-800">¿Cómo funcionan las Penalidades?</h4>
                <div class="text-sm space-y-3 text-blue-800">
                  <p><strong>Propósito:</strong> Las penalidades (fees) son cargos adicionales que se aplican al plan de crédito.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-2">Tipos de Listas:</p>
                    <ul class="list-disc list-inside space-y-2 ml-2">
                      <li><strong>Lista Blanca:</strong> Solo estas penalidades específicas se pueden aplicar al plan</li>
                      <li><strong>Lista Negra:</strong> Estas penalidades NO se pueden aplicar al plan (están excluidas)</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-3 rounded bg-blue-100 border border-blue-200">
                    <p class="font-medium text-sm mb-2">🔍 Reglas de Aplicación:</p>
                    <div class="space-y-2 text-xs">
                      <div class="p-2 bg-white rounded">
                        <p class="font-medium text-green-700 mb-1">✓ Si la penalidad está ACTIVA en el producto:</p>
                        <p>Aplica a <strong>todos los planes</strong> automáticamente, excepto si se agrega a la <strong>Lista Negra</strong> del plan.</p>
                      </div>
                      <div class="p-2 bg-white rounded">
                        <p class="font-medium text-orange-700 mb-1">⊗ Si la penalidad está INACTIVA en el producto:</p>
                        <p>Solo aplica si se agrega explícitamente a la <strong>Lista Blanca</strong> del plan.</p>
                      </div>
                    </div>
                  </div>

                  <div class="mt-3 p-2 rounded bg-blue-100">
                    <p class="font-medium text-xs">💡 Tip:</p>
                    <p class="text-xs">Si un plan no tiene listas configuradas, se aplican todas las penalidades activas del sistema</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lista Blanca -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <i class="fas fa-check-circle text-green-600 mr-2"></i>
              Lista Blanca de Penalidades
            </h3>
            <div *ngIf="getWhiteListFees().length > 0; else noWhiteListFeesDetail">
              <div class="space-y-2 mb-4">
                <div *ngFor="let feeCode of getWhiteListFees()"
                      class="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ feeCode }}</div>
                    <div class="text-sm text-gray-600">{{ getFeeName(feeCode) }}</div>
                  </div>
                  <i class="fas fa-check-circle text-green-600"></i>
                </div>
              </div>
              <p class="text-sm text-gray-600">
                <span class="font-medium">{{ getWhiteListFees().length }}</span>
                {{ getWhiteListFees().length === 1 ? 'penalidad permitida' : 'penalidades permitidas' }}
              </p>
            </div>
            <ng-template #noWhiteListFeesDetail>
              <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-check-circle text-gray-400 text-2xl mb-2"></i>
                <p class="text-sm text-gray-500">No hay penalidades en la Lista Blanca</p>
                <p class="text-xs text-gray-400 mt-1">Se pueden aplicar todas las penalidades (excepto las de Lista Negra)</p>
              </div>
            </ng-template>
          </div>

          <!-- Lista Negra -->
          <div class="mb-6">
            <h3 class="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <i class="fas fa-ban text-red-600 mr-2"></i>
              Lista Negra de Penalidades
            </h3>
            <div *ngIf="getBlackListFees().length > 0; else noBlackListFeesDetail">
              <div class="space-y-2 mb-4">
                <div *ngFor="let feeCode of getBlackListFees()"
                      class="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div class="flex-1">
                    <div class="font-medium text-gray-900">{{ feeCode }}</div>
                    <div class="text-sm text-gray-600">{{ getFeeName(feeCode) }}</div>
                  </div>
                  <i class="fas fa-ban text-red-600"></i>
                </div>
              </div>
              <p class="text-sm text-gray-600">
                <span class="font-medium">{{ getBlackListFees().length }}</span>
                {{ getBlackListFees().length === 1 ? 'penalidad excluida' : 'penalidades excluidas' }}
              </p>
            </div>
            <ng-template #noBlackListFeesDetail>
              <div class="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <i class="fas fa-ban text-gray-400 text-2xl mb-2"></i>
                <p class="text-sm text-gray-500">No hay penalidades en la Lista Negra</p>
                <p class="text-xs text-gray-400 mt-1">No hay penalidades excluidas para este plan</p>
              </div>
            </ng-template>
          </div>

          <!-- Resumen -->
          <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-blue-800">Resumen de Configuración</h4>
                <div class="text-sm text-blue-800 space-y-1">
                  <p *ngIf="getWhiteListFees().length > 0">
                    <strong>✓</strong> Solo se pueden aplicar {{ getWhiteListFees().length }} 
                    {{ getWhiteListFees().length === 1 ? 'penalidad específica' : 'penalidades específicas' }}
                  </p>
                  <p *ngIf="getBlackListFees().length > 0">
                    <strong>✗</strong> Se excluyen {{ getBlackListFees().length }} 
                    {{ getBlackListFees().length === 1 ? 'penalidad' : 'penalidades' }}
                  </p>
                  <p *ngIf="getWhiteListFees().length === 0 && getBlackListFees().length === 0" class="text-gray-600">
                    Sin restricciones configuradas - Se pueden aplicar todas las penalidades del sistema
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Productos Section -->
        <div id="section-productos" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'productos'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">🛍️ Productos</h2>
              <p class="text-sm text-gray-500 mt-1">Identificadores asignados a este plan</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('productos')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['productos']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['productos'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['productos']" class="mb-6 rounded-lg p-4 bg-green-50 border border-green-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-green-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-green-800">¿Cómo funcionan los Productos?</h4>
                <div class="text-sm space-y-2 text-green-800">
                  <p><strong>Propósito:</strong> Los identificadores determinan a qué productos se aplica este plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Tipos Disponibles:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><code class="bg-white px-1 rounded">CREDIT</code> - Productos de crédito básico</li>
                      <li><code class="bg-white px-1 rounded">RECHARGE</code> - Productos de recarga</li>
                      <li><code class="bg-white px-1 rounded">SEGUROS_MERCANTIL</code> - Seguros del Banco Mercantil</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Funcionamiento:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Restricción:</strong> Solo se puede asignar UN identificador por plan</li>
                      <li><strong>Herencia:</strong> El plan hereda las características del producto</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-green-100">
                    <p class="font-medium text-xs">💡 Tip:</p>
                    <p class="text-xs">Solo se puede asignar UN identificador por plan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Identificador Principal -->
          <div *ngIf="plan.params?.product_type; else noProductSelected">
            <!-- Card Simple -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i class="fas fa-tag text-blue-600"></i>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-gray-900">{{ plan.params.product_type }}</h3>
                    <p class="text-sm text-gray-600">
                      <span *ngIf="parentProductName && parentProductName !== plan.params.product_type">
                        {{ parentProductName }}
                      </span>
                      <span *ngIf="!parentProductName" class="text-gray-400">
                        Producto no encontrado en el sistema
                      </span>
                      <span *ngIf="parentProductName === plan.params.product_type" class="text-gray-500">
                        Identificador de producto
                      </span>
                    </p>
                  </div>
                </div>
                <div class="text-right">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <i class="fas fa-check-circle mr-1"></i>
                    Activo
                  </span>
                </div>
              </div>
              
              <!-- Información de Seguros (si aplica) -->
              <div *ngIf="plan.params?.insurance && (plan.params.insurance.plan || plan.params.insurance.brokerage)" 
                   class="mt-4 pt-4 border-t border-blue-300">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Propuesta de Seguro -->
                  <div *ngIf="plan.params.insurance.plan" class="bg-white rounded-lg p-3">
                    <div class="flex items-start">
                      <div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <i class="fas fa-file-contract text-purple-600 text-sm"></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-medium text-gray-500 mb-1">Propuesta de Seguro</p>
                        <p class="text-sm font-semibold text-gray-900" [title]="getInsuranceProposalName(plan.params.insurance.plan)">
                          {{ getInsuranceProposalName(plan.params.insurance.plan) }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1 font-mono">ID: {{ plan.params.insurance.plan }}</p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Aseguradora -->
                  <div *ngIf="plan.params.insurance.brokerage" class="bg-white rounded-lg p-3">
                    <div class="flex items-start">
                      <div class="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <i class="fas fa-shield-alt text-green-600 text-sm"></i>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-medium text-gray-500 mb-1">Aseguradora</p>
                        <p class="text-sm font-semibold text-gray-900 truncate" [title]="getInsurerName(plan.params.insurance.brokerage)">
                          {{ getInsurerName(plan.params.insurance.brokerage) }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1">Código: {{ plan.params.insurance.brokerage }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ng-template #noProductSelected>
            <div class="text-center py-8 bg-yellow-50 rounded-lg border border-yellow-200">
              <i class="fas fa-exclamation-triangle text-yellow-500 text-2xl mb-2"></i>
              <p class="text-sm text-yellow-700">Sin identificador configurado</p>
              <button class="mt-2 px-4 py-2 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700">
                Configurar
              </button>
            </div>
          </ng-template>

          <!-- Acciones Simples -->
          <div class="flex space-x-3 pt-4 border-t border-gray-200">
            <button class="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <i class="fas fa-edit mr-2"></i>
              Cambiar ID
            </button>
            <button class="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <i class="fas fa-list mr-2"></i>
              Ver Todos
            </button>
            <button class="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              <i class="fas fa-download mr-2"></i>
              Exportar
            </button>
          </div>
        </div>

        <!-- Datos Básicos Section -->
        <div id="section-datos-basicos" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'datos-basicos'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Datos Básicos</h2>
              <p class="text-sm text-gray-500 mt-1">Información fundamental del plan</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('datos-basicos')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['datos-basicos']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['datos-basicos'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['datos-basicos']" class="mb-6 rounded-lg p-4 bg-purple-50 border border-purple-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-purple-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-purple-800">¿Qué son los Datos Básicos?</h4>
                <div class="text-sm space-y-2 text-purple-800">
                  <p><strong>Propósito:</strong> Define la identidad y configuración fundamental del plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Campos Principales:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Nombre:</strong> Identificación amigable del plan (ej: "Plan Empleados 2024")</li>
                      <li><strong>Código:</strong> Identificador único inmutable (ej: "EMP_2024_V1")</li>
                      <li><strong>Descripción:</strong> Detalles del plan para clientes</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Estados del Plan:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><span class="text-green-600">●</span> <strong>Activo:</strong> Visible y disponible para clientes</li>
                      <li><span class="text-red-600">●</span> <strong>Inactivo:</strong> Oculto para clientes</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-yellow-100">
                    <p class="font-medium text-xs">⚠️ Importante:</p>
                    <p class="text-xs">El código NO se puede cambiar una vez creado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Información Simple -->
          <div class="space-y-3">

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Tipo:</span>
                <div class="ml-2 flex items-center">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        [class.bg-blue-100]="isPlanCredit()"
                        [class.text-blue-800]="isPlanCredit()"
                        [class.bg-green-100]="!isPlanCredit()"
                        [class.text-green-800]="!isPlanCredit()">
                    <i class="mr-1"
                       [class.fas]="true"
                       [class.fa-credit-card]="isPlanCredit()"
                       [class.fa-money-bill-wave]="!isPlanCredit()"></i>
                    {{ getPlanTypeText() }}
                  </span>
                </div>
              </div>
              <div>
                <span class="text-sm text-gray-500">Estado del Plan:</span>
                <span class="text-sm ml-2 font-medium"
                      [class.text-green-600]="plan.status === true || plan.status === 'ACTIVE'"
                      [class.text-red-600]="plan.status === false || plan.status === 'INACTIVE'">
                  {{ (plan.status === true || plan.status === 'ACTIVE') ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Nombre:</span>
                <span class="text-sm text-gray-900 ml-2">{{ plan.name }}</span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Código:</span>
                <span class="text-sm text-gray-900 ml-2 font-mono">{{ plan.code }}</span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Multicompra:</span>
                <span class="text-sm text-gray-900 ml-2">
                  {{ plan.params?.multiple_invoices?.status ? 'Sí' : 'No' }}
                  <span *ngIf="plan.params?.multiple_invoices?.status && plan.params?.multiple_invoices?.count > 0" class="text-gray-400">
                    ({{ plan.params?.multiple_invoices?.count }})
                  </span>
                </span>
              </div>
              <!-- OCULTAR VISIBILIDAD POR SOLICITUD DEL USUARIO
              <div class="flex justify-between">
                <span class="text-gray-600">Visibilidad:</span>
                <span class="text-gray-900">{{ getVisibilityText(plan.visibility) }}</span>
              </div>
              -->
              <div class="flex justify-between">
                <span class="text-gray-600">Versión:</span>
                <span class="text-gray-900 font-mono">{{ plan.version?.internal_code || 'v1.0' }}</span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Prioridad:</span>
                <span class="text-sm text-gray-900 ml-2">{{ plan.params?.priority || 0 }}</span>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Creado:</span>
                <span class="text-sm text-gray-900 ml-2">{{ formatDate(plan.date) }}</span>
              </div>
              <div *ngIf="plan.params?.init_date || plan.params?.end_date">
                <span class="text-sm text-gray-500">Vigencia:</span>
                <span class="text-sm text-gray-900 ml-2">
                  <span *ngIf="plan.params?.init_date">{{ formatDate(plan.params.init_date) }}</span>
                  <span *ngIf="plan.params?.init_date && plan.params?.end_date"> - </span>
                  <span *ngIf="plan.params?.end_date">{{ formatDate(plan.params.end_date) }}</span>
                  <span *ngIf="!plan.params?.end_date && plan.params?.init_date"> - Sin límite</span>
                </span>
              </div>
            </div>

            <div *ngIf="plan.description" class="pt-3 border-t border-gray-100">
              <span class="text-sm text-gray-500">Descripción:</span>
              <p class="text-sm text-gray-900 mt-1" [innerHTML]="plan.description"></p>
            </div>

            <!-- Icono del plan si existe -->
            <div *ngIf="plan.params?.icon" class="pt-3 border-t border-gray-100">
              <span class="text-sm text-gray-500">Icono:</span>
              <div class="flex items-center mt-1">
                <i [class]="plan.params.icon + ' text-2xl text-cyan-600 mr-3'"></i>
                <span class="text-sm text-gray-600 font-mono">{{ plan.params.icon }}</span>
              </div>
            </div>
          </div>

        </div>

        <!-- Datos Generales Section -->
        <div id="section-datos-generales" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'datos-generales' && isSectionVisible('datos-generales')">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Datos Generales</h2>
              <p class="text-sm text-gray-500 mt-1">Configuración financiera y términos de crédito</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('datos-generales')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['datos-generales']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['datos-generales'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['datos-generales']" class="mb-6 rounded-lg p-4 bg-orange-50 border border-orange-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-orange-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-orange-800">¿Cómo configurar los Datos Generales?</h4>
                <div class="text-sm space-y-2 text-orange-800">
                  <p><strong>Propósito:</strong> Configuración financiera y términos de crédito del plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Pago Inicial:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Requiere inicial:</strong> Si el plan exige pago inicial</li>
                      <li><strong>Tipo de cálculo:</strong> Porcentaje o monto fijo (ej: 20% o $50)</li>
                      <li><strong>Recalcular cuotas:</strong> Si ajusta cuotas tras el inicial</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Plazos y Cuotas:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Opciones de cuotas:</strong> Cantidad de cuotas disponibles (ej: 2, 3, 4 cuotas)</li>
                      <li><strong>Frecuencia:</strong> Periodicidad de pago (ej: Mensual, quincenal)</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Límites de Cupo:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Cupo mínimo/máximo:</strong> Rango de cupo asignable</li>
                      <li><strong>Monto mín/máx a financiar:</strong> Límites por transacción</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 bg-white rounded border">
                    <p class="font-medium text-xs mb-1">Ejemplo Plan Empleados:</p>
                    <ul class="text-xs space-y-1">
                      <li>• Inicial: 20% del monto</li>
                      <li>• Cuotas: 2, 3, 4 opciones</li>
                      <li>• Cupo: $500 - $2,000</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-orange-100">
                    <p class="font-medium text-xs">💡 Tip:</p>
                    <p class="text-xs">Define términos de crédito y límites según el perfil de usuario</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pago Inicial -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Pago Inicial</h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Requiere Inicial:</span>
                <span class="text-sm ml-2 font-medium"
                      [class.text-green-600]="plan.initial?.status"
                      [class.text-red-600]="!plan.initial?.status">
                  {{ plan.initial?.status ? 'Sí' : 'No' }}
                </span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Forma de Cálculo:</span>
                <span class="text-sm text-gray-900 ml-2">
                  {{ plan.initial?.type === 'percentage' ? 'Porcentaje' : 'Monto Absoluto' }}
                </span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Valor Inicial:</span>
                <span class="text-sm text-gray-900 ml-2 font-medium">
                  {{ plan.initial?.amount }}{{ plan.initial?.type === 'percentage' ? '%' : ' USD' }}
                </span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Monto Mínimo de Compra:</span>
                <span class="text-sm text-gray-900 ml-2">${{ plan.params?.min_amount || 'No definido' }}</span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Recalcular Cuotas:</span>
                <span class="text-sm ml-2 font-medium"
                      [class.text-green-600]="plan.initial?.recalculate"
                      [class.text-red-600]="!plan.initial?.recalculate">
                  {{ plan.initial?.recalculate ? 'Sí' : 'No' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Plazos -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Plazos</h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Opciones de Cuotas:</span>
                <span class="text-sm text-gray-900 ml-2">
                  <span *ngFor="let term of plan.payment_terms; let last = last" class="font-medium">
                    {{ term.installments }}<span *ngIf="!last">, </span>
                  </span>
                </span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Frecuencia:</span>
                <span class="text-sm text-gray-900 ml-2">
                  {{ plan.payment_terms?.[0]?.frequency?.type === 'mes' ? 'Mensual' :
                     plan.payment_terms?.[0]?.frequency?.type === 'quincenal' ? 'Quincenal' :
                     plan.payment_terms?.[0]?.frequency?.type === 'semanal' ? 'Semanal' :
                     plan.payment_terms?.[0]?.frequency?.type === 'semana' ? 'Semanal' :
                     'Días continuos' }}
                </span>
              </div>
            </div>
          </div>

          <!-- Cupo -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Cupo</h3>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-sm text-gray-500">Cupo Mínimo:</span>
                <span class="text-sm text-gray-900 ml-2">${{ plan.params?.current_quota_min || 'No definido' }}</span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Cupo Máximo:</span>
                <span class="text-sm text-gray-900 ml-2">${{ plan.params?.current_quota_max || 'No definido' }}</span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Monto Mínimo a Financiar:</span>
                <span class="text-sm text-gray-900 ml-2">${{ plan.params?.min_amount_base || 'No definido' }}</span>
              </div>
              <div>
                <span class="text-sm text-gray-500">Monto Máximo a Financiar:</span>
                <span class="text-sm text-gray-900 ml-2">${{ plan.params?.max_amount_base || 'No definido' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Clients Section -->
        <div id="section-clientes" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'clientes'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Clientes Activos</h2>
              <p class="text-sm text-gray-500 mt-1">Usuarios que actualmente tienen créditos con este plan</p>
            </div>
            <div class="flex space-x-2">
              <!-- Botón de ayuda solo visible en pantallas menores a XL -->
              <button (click)="toggleHelp('clientes')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors xl:hidden"
                      [class.text-blue-600]="showHelp['clientes']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['clientes'] ? 'Ocultar' : 'Ayuda' }}
              </button>
              <button class="text-sm text-blue-600 hover:text-blue-800 font-medium">
                <i class="fas fa-download mr-1"></i>
                Exportar
              </button>
              <button class="text-sm text-blue-600 hover:text-blue-800 font-medium">
                <i class="fas fa-filter mr-1"></i>
                Filtrar
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['clientes']" class="mb-6 rounded-lg p-4 bg-indigo-50 border border-indigo-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-indigo-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-indigo-800">¿Cómo interpretar los Clientes?</h4>
                <div class="text-sm space-y-2 text-indigo-800">
                  <p><strong>Propósito:</strong> Usuarios que actualmente tienen créditos activos con este plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Estados de Cliente:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><span class="text-green-600">●</span> <strong>Al día:</strong> Pagos al corriente</li>
                      <li><span class="text-red-600">●</span> <strong>Atrasado:</strong> Pagos en mora</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Información Mostrada:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Crédito activo:</strong> Monto actual del crédito</li>
                      <li><strong>Progreso:</strong> Cuotas pagadas vs total</li>
                      <li><strong>Fecha inicio:</strong> Cuándo se otorgó el crédito</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-indigo-100">
                    <p class="font-medium text-xs">📊 Análisis:</p>
                    <p class="text-xs">Evalúa el rendimiento y riesgo del plan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="clientsLoading" class="text-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p class="text-gray-600 mt-2">Cargando clientes...</p>
          </div>

          <div *ngIf="!clientsLoading && activeClients.length > 0" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crédito</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr *ngFor="let client of activeClients; trackBy: trackByClientId" class="hover:bg-gray-50">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-gray-900">{{ client.name }}</div>
                      <div class="text-sm text-gray-500">{{ client.email }}</div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatCurrency(client.creditoActivo) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          [ngClass]="getClientStatusBadgeClass(client.estado)">
                      {{ client.estado }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div class="bg-blue-600 h-2 rounded-full"
                             [style.width.%]="(client.cuotasPagadas / client.cuotasTotal) * 100"></div>
                      </div>
                      <span class="text-sm text-gray-600">{{ client.cuotasPagadas }}/{{ client.cuotasTotal }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-blue-600 hover:text-blue-900 mr-3" title="Ver detalles del cliente">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button class="text-gray-600 hover:text-gray-900" title="Contactar cliente">
                      <i class="fas fa-envelope"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div *ngIf="!clientsLoading && activeClients.length === 0" class="text-center py-12">
            <i class="fas fa-users text-gray-400 text-4xl mb-4"></i>
            <p class="text-gray-600 text-lg">No hay clientes activos</p>
            <p class="text-gray-500 text-sm">Este plan aún no tiene clientes con créditos activos</p>
          </div>
        </div>

        <!-- Version History Section -->
        <div id="section-historial" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'historial'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Historial de Versiones</h2>
              <p class="text-sm text-gray-500 mt-1">Registro de cambios y modificaciones realizadas al plan</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <button (click)="toggleHelp('historial')"
                    class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors xl:hidden"
                    [class.text-blue-600]="showHelp['historial']">
              <i class="fas fa-question-circle mr-1"></i>
              {{ showHelp['historial'] ? 'Ocultar' : 'Ayuda' }}
            </button>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['historial']" class="mb-6 rounded-lg p-4 bg-gray-50 border border-gray-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-gray-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-gray-800">¿Para qué sirve el Historial?</h4>
                <div class="text-sm space-y-2 text-gray-800">
                  <p><strong>Propósito:</strong> Registro completo de cambios y modificaciones del plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Tipos de Cambios:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Modificaciones:</strong> Cambios en configuración</li>
                      <li><strong>Versiones:</strong> Nuevas versiones del plan</li>
                      <li><strong>Estados:</strong> Activación/desactivación</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Información de Auditoría:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Fecha y hora:</strong> Cuándo se realizó el cambio</li>
                      <li><strong>Usuario:</strong> Quién realizó la modificación</li>
                      <li><strong>Detalles:</strong> Qué campos se modificaron</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-gray-100">
                    <p class="font-medium text-xs">🔍 Auditoría:</p>
                    <p class="text-xs">Esencial para cumplimiento y trazabilidad</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="historyLoading" class="text-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p class="text-gray-600 mt-2">Cargando historial...</p>
          </div>

          <div *ngIf="!historyLoading" class="space-y-6">
            <div *ngFor="let version of versionHistory; let i = index"
                 class="relative pl-6 pb-6"
                 [class.border-l-2]="i < versionHistory.length - 1"
                 [class.border-gray-200]="i < versionHistory.length - 1">

              <!-- Timeline dot -->
              <div class="absolute left-0 top-0 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow"></div>

              <div class="bg-gray-50 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="text-sm font-semibold text-gray-900">{{ version.version.internal_code }}</h3>
                  <span class="text-xs text-gray-500 bg-white px-2 py-1 rounded">{{ formatDate(version.version.date_init_version) }}</span>
                </div>

                <div class="space-y-2 mb-3">
                  <div *ngFor="let change of version.changes" class="text-sm">
                    <span class="font-medium text-gray-700">{{ change.field }}:</span>
                    <span class="text-gray-600 ml-1">{{ change.newValue }}</span>
                  </div>
                </div>

                <div class="flex items-center text-xs text-gray-500">
                  <i class="fas fa-user mr-1"></i>
                  <span>Modificado por: {{ version.user }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Metrics Section -->
        <div id="section-metricas" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'metricas'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">Métricas y Análisis</h2>
              <p class="text-sm text-gray-500 mt-1">Indicadores de rendimiento y estadísticas del plan</p>
            </div>
            <div class="flex space-x-2">
              <!-- Botón de ayuda solo visible en pantallas menores a XL -->
              <button (click)="toggleHelp('metricas')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors xl:hidden"
                      [class.text-blue-600]="showHelp['metricas']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['metricas'] ? 'Ocultar' : 'Ayuda' }}
              </button>
              <select class="text-sm border-gray-300 rounded-md">
                <option>Último mes</option>
                <option>Últimos 3 meses</option>
                <option>Último año</option>
              </select>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['metricas']" class="mb-6 rounded-lg p-4 bg-teal-50 border border-teal-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-teal-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-teal-800">¿Cómo interpretar las Métricas?</h4>
                <div class="text-sm space-y-2 text-teal-800">
                  <p><strong>Propósito:</strong> Indicadores de rendimiento y estadísticas del plan.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Indicadores Clave:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><span class="text-green-600">●</span> <strong>Tasa de aprobación:</strong> Porcentaje de solicitudes aprobadas</li>
                      <li><strong>Promedio por crédito:</strong> Monto promedio otorgado</li>
                      <li><strong>Ingresos del mes:</strong> Ingresos generados por el plan</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Interpretación:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><span class="text-green-600">●</span> <strong>Verde:</strong> Indicadores positivos</li>
                      <li><span class="text-red-600">●</span> <strong>Rojo:</strong> Requiere atención</li>
                      <li><strong>Tendencias:</strong> Observa cambios en el tiempo</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-teal-100">
                    <p class="font-medium text-xs">📈 Optimización:</p>
                    <p class="text-xs">Usa estas métricas para mejorar el rendimiento del plan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
              <div class="flex items-center">
                <i class="fas fa-percentage text-blue-600 text-2xl mr-4 flex-shrink-0"></i>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-blue-800 truncate">Tasa de Aprobación</p>
                  <p [ngClass]="getDynamicTextSizeClass(formatPercentage(metrics.tasaAprobacion))" class="text-blue-900">{{ formatPercentage(metrics.tasaAprobacion) }}</p>
                  <p class="text-xs text-blue-700 mt-1 truncate">De solicitudes recibidas</p>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
              <div class="flex items-center">
                <i class="fas fa-chart-line text-green-600 text-2xl mr-4 flex-shrink-0"></i>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-green-800 truncate">Promedio por Crédito</p>
                  <p [ngClass]="getDynamicCurrencySize(metrics.promedioMonto)" class="text-green-900">{{ formatCurrency(metrics.promedioMonto) }}</p>
                  <p class="text-xs text-green-700 mt-1 truncate">Monto promedio otorgado</p>
                </div>
              </div>
            </div>

            <div class="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
              <div class="flex items-center">
                <i class="fas fa-coins text-purple-600 text-2xl mr-4 flex-shrink-0"></i>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-purple-800 truncate">Ingresos del Mes</p>
                  <p [ngClass]="getDynamicCurrencySize(metrics.ingresosMes)" class="text-purple-900">{{ formatCurrency(metrics.ingresosMes) }}</p>
                  <p class="text-xs text-purple-700 mt-1 truncate">Ingresos generados</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Otros Section -->
        <div id="section-otros" class="bg-white rounded-lg shadow-sm border p-6" *ngIf="activeSection === 'otros'">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-900">⚙️ Otros</h2>
              <p class="text-sm text-gray-500 mt-1">Configuración adicional de documentación legal y políticas</p>
            </div>
            <!-- Botón de ayuda solo visible en pantallas menores a XL -->
            <div class="flex space-x-2 xl:hidden">
              <button (click)="toggleHelp('otros')"
                      class="text-sm text-gray-600 hover:text-blue-600 font-medium transition-colors"
                      [class.text-blue-600]="showHelp['otros']">
                <i class="fas fa-question-circle mr-1"></i>
                {{ showHelp['otros'] ? 'Ocultar' : 'Ayuda' }}
              </button>
            </div>
          </div>

          <!-- Ayuda Contextual - Solo visible en pantallas menores a XL -->
          <div *ngIf="showHelp['otros']" class="mb-6 rounded-lg p-4 bg-yellow-50 border border-yellow-200 xl:hidden">
            <div class="flex items-start">
              <i class="fas fa-info-circle text-yellow-600 mt-1 mr-3"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold mb-2 text-yellow-800">¿Qué configurar en Otros?</h4>
                <div class="text-sm space-y-2 text-yellow-800">
                  <p><strong>Propósito:</strong> Configuración adicional de documentación legal y políticas de cobranza.</p>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Documentación Legal:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Pagaré Electrónico:</strong> Genera automáticamente documento digital</li>
                      <li><strong>Pagaré Físico:</strong> Requiere documento físico firmado</li>
                    </ul>
                  </div>

                  <div class="mt-3">
                    <p class="font-medium mb-1">Políticas de Cobranza:</p>
                    <ul class="list-disc list-inside space-y-1 ml-2">
                      <li><strong>Permitir cuotas en feriados:</strong> Si las fechas de pago pueden caer en fines de semana o feriados</li>
                      <li><strong>Ajuste automático:</strong> Mueve fechas al siguiente día hábil si está desactivado</li>
                      <li><strong>Diferir cuotas en feriados:</strong> Mueve automáticamente las cuotas de fines de semana y feriados al siguiente día hábil</li>
                      <li><strong>Permitir en cualquier día:</strong> Las fechas pueden caer en cualquier día si está desactivado</li>
                    </ul>
                  </div>

                  <div class="mt-3 p-2 rounded bg-yellow-100">
                    <p class="font-medium text-xs">⚖️ Legal:</p>
                    <p class="text-xs">Los pagarés son documentos legales que respaldan el crédito</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Documentación Legal -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-file-contract text-blue-600 mr-2"></i>
              Documentación Legal
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Pagaré Electrónico -->
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <i class="fas fa-file-digital text-blue-600 mr-2"></i>
                    <h4 class="text-md font-medium text-gray-900">Pagaré Electrónico</h4>
                  </div>
                  <div class="flex items-center">
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                          [class.bg-green-100]="plan.params?.generate_pagares"
                          [class.text-green-800]="plan.params?.generate_pagares"
                          [class.bg-gray-100]="!plan.params?.generate_pagares"
                          [class.text-gray-800]="!plan.params?.generate_pagares">
                      <i class="fas text-xs mr-1"
                         [class.fa-check-circle]="plan.params?.generate_pagares"
                         [class.fa-times-circle]="!plan.params?.generate_pagares"></i>
                      {{ plan.params?.generate_pagares ? 'Activado' : 'Desactivado' }}
                    </span>
                  </div>
                </div>
                <p class="text-sm text-gray-600 mb-3">
                  {{ plan.params?.generate_pagares ?
                     'Se generará automáticamente un pagaré digital al formalizar el crédito.' :
                     'No se generará pagaré electrónico para este plan.' }}
                </p>
                <div class="flex items-center text-xs text-gray-500">
                  <i class="fas fa-info-circle mr-1"></i>
                  <span>{{ plan.params?.generate_pagares ? 'Proceso automático y ágil' : 'Sin documentación digital' }}</span>
                </div>
              </div>

              <!-- Pagaré Físico -->
              <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center">
                    <i class="fas fa-file-signature text-orange-600 mr-2"></i>
                    <h4 class="text-md font-medium text-gray-900">Pagaré Físico</h4>
                  </div>
                  <div class="flex items-center">
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                          [class.bg-green-100]="plan.params?.generate_pagares_fisico"
                          [class.text-green-800]="plan.params?.generate_pagares_fisico"
                          [class.bg-gray-100]="!plan.params?.generate_pagares_fisico"
                          [class.text-gray-800]="!plan.params?.generate_pagares_fisico">
                      <i class="fas text-xs mr-1"
                         [class.fa-check-circle]="plan.params?.generate_pagares_fisico"
                         [class.fa-times-circle]="!plan.params?.generate_pagares_fisico"></i>
                      {{ plan.params?.generate_pagares_fisico ? 'Activado' : 'Desactivado' }}
                    </span>
                  </div>
                </div>
                <p class="text-sm text-gray-600 mb-3">
                  {{ plan.params?.generate_pagares_fisico ?
                     'Se requerirá la firma de un pagaré físico como respaldo adicional del crédito.' :
                     'No se requiere pagaré físico para este plan.' }}
                </p>
                <div class="flex items-center text-xs text-gray-500">
                  <i class="fas fa-info-circle mr-1"></i>
                  <span>{{ plan.params?.generate_pagares_fisico ? 'Requiere proceso presencial' : 'Sin documentación física' }}</span>
                </div>
              </div>
            </div>

            <!-- Resumen de Documentación -->
            <div class="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <i class="fas fa-clipboard-check text-gray-600 mr-2"></i>
                  <span class="text-sm font-medium text-gray-700">Estado de Documentación:</span>
                </div>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-600">
                    <span *ngIf="plan.params?.generate_pagares || plan.params?.generate_pagares_fisico; else noDocuments">
                      {{ (plan.params?.generate_pagares ? 1 : 0) + (plan.params?.generate_pagares_fisico ? 1 : 0) }}
                      tipo{{ (plan.params?.generate_pagares && plan.params?.generate_pagares_fisico) ? 's' : '' }} configurado{{ (plan.params?.generate_pagares && plan.params?.generate_pagares_fisico) ? 's' : '' }}
                    </span>
                    <ng-template #noDocuments>
                      <span class="text-yellow-600">Sin documentación configurada</span>
                    </ng-template>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Políticas de Cobranza (Solo para planes A CRÉDITO) -->
          <div class="mb-8" *ngIf="isPlanCredit()">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-calendar-alt text-green-600 mr-2"></i>
              Políticas de Cobranza
            </h3>

            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center">
                  <i class="fas fa-calendar-week text-green-600 mr-2"></i>
                  <h4 class="text-md font-medium text-gray-900">Diferimiento de Cuotas en Fines de Semana y Feriados</h4>
                </div>
                <div class="flex items-center">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        [class.bg-green-100]="plan.params?.weekday"
                        [class.text-green-800]="plan.params?.weekday"
                        [class.bg-red-100]="!plan.params?.weekday"
                        [class.text-red-800]="!plan.params?.weekday">
                    <i class="fas text-xs mr-1"
                       [class.fa-check-circle]="plan.params?.weekday"
                       [class.fa-times-circle]="!plan.params?.weekday"></i>
                    {{ plan.params?.weekday ? 'Diferir al día hábil' : 'Permitir en fines de semana' }}
                  </span>
                </div>
              </div>

              <p class="text-sm text-gray-600 mb-3">
                <span *ngIf="plan.params?.weekday; else noWeekendPayments">
                  Las fechas de vencimiento de cuotas que caigan en fines de semana o feriados se diferirán automáticamente al siguiente día hábil.
                </span>
                <ng-template #noWeekendPayments>
                  Las fechas de vencimiento de cuotas pueden caer en cualquier día, incluyendo fines de semana y feriados.
                </ng-template>
              </p>

              <div class="flex items-start space-x-2 text-xs text-gray-500">
                <i class="fas fa-lightbulb mt-0.5"></i>
                <div>
                  <p class="font-medium mb-1">Recomendación:</p>
                  <p *ngIf="!plan.params?.weekday">
                    <span class="text-green-600">✓ Configuración recomendada</span> - Evita confusiones con fechas de pago en días no laborables.
                  </p>
                  <p *ngIf="plan.params?.weekday">
                    <span class="text-yellow-600">⚠ Considerar cambio</span> - Puede generar confusión en clientes sobre fechas de pago efectivas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Mensaje para Planes DE CONTADO -->
          <div class="mb-8" *ngIf="!isPlanCredit()">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="flex items-center">
                <i class="fas fa-info-circle text-blue-600 mr-3"></i>
                <div>
                  <h4 class="text-sm font-medium text-blue-800 mb-1">Plan de Contado</h4>
                  <p class="text-sm text-blue-700">
                    Las políticas de cobranza no aplican para planes de contado ya que no manejan cuotas.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Visibilidad del Plan al Cliente -->
          <div class="mb-8">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-eye text-cyan-600 mr-2"></i>
              Visibilidad del Plan al Cliente
            </h3>

            <div class="border rounded-lg p-4"
                 [class.bg-cyan-50]="plan.params?.client_visible !== false"
                 [class.border-cyan-200]="plan.params?.client_visible !== false"
                 [class.bg-gray-50]="plan.params?.client_visible === false"
                 [class.border-gray-300]="plan.params?.client_visible === false">
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center">
                  <i class="text-lg mr-2"
                     [class.fas]="true"
                     [class.fa-eye]="plan.params?.client_visible !== false"
                     [class.text-cyan-600]="plan.params?.client_visible !== false"
                     [class.fa-eye-slash]="plan.params?.client_visible === false"
                     [class.text-gray-600]="plan.params?.client_visible === false"></i>
                  <h4 class="text-md font-medium text-gray-900">Plan {{ plan.params?.client_visible === false ? 'Oculto' : 'Visible' }} para Clientes</h4>
                </div>
                <div class="flex items-center">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                        [class.bg-cyan-100]="plan.params?.client_visible !== false"
                        [class.text-cyan-800]="plan.params?.client_visible !== false"
                        [class.bg-gray-200]="plan.params?.client_visible === false"
                        [class.text-gray-700]="plan.params?.client_visible === false">
                    <i class="fas text-xs mr-1"
                       [class.fa-check-circle]="plan.params?.client_visible !== false"
                       [class.fa-ban]="plan.params?.client_visible === false"></i>
                    {{ plan.params?.client_visible === false ? 'Oculto' : 'Visible' }}
                  </span>
                </div>
              </div>

              <p class="text-sm text-gray-600 mb-3">
                <span *ngIf="plan.params?.client_visible !== false; else planHidden">
                  Este plan está visible para los clientes en la aplicación móvil y web. Los clientes pueden ver y solicitar este plan.
                </span>
                <ng-template #planHidden>
                  Este plan está oculto para los clientes. Solo los administradores pueden verlo en la consola. Los clientes no podrán solicitar este plan.
                </ng-template>
              </p>

              <div class="flex items-start space-x-2 text-xs text-gray-500">
                <i class="fas fa-info-circle mt-0.5"></i>
                <div>
                  <p class="font-medium mb-1">Información:</p>
                  <p *ngIf="plan.params?.client_visible !== false">
                    <span class="text-cyan-600">✓ Disponible para solicitudes</span> - Los clientes pueden ver este plan en sus opciones de financiamiento.
                  </p>
                  <p *ngIf="plan.params?.client_visible === false">
                    <span class="text-gray-600">⊘ No disponible para solicitudes</span> - Este plan solo es visible en la consola administrativa.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Resumen de Configuración -->
          <div class="mt-8 pt-6 border-t border-gray-200">
            <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <i class="fas fa-cog text-purple-600 mr-2"></i>
              Resumen de Configuración
            </h3>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div class="flex items-center">
                  <i class="fas fa-file-contract text-purple-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-purple-900">Documentación</p>
                    <p class="text-lg font-semibold text-purple-800">
                      {{ (plan.params?.generate_pagares ? 1 : 0) + (plan.params?.generate_pagares_fisico ? 1 : 0) }}/2
                    </p>
                    <p class="text-xs text-purple-700">Tipos configurados</p>
                  </div>
                </div>
              </div>

              <div class="bg-green-50 p-4 rounded-lg border border-green-200" *ngIf="isPlanCredit()">
                <div class="flex items-center">
                  <i class="fas fa-calendar-check text-green-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-green-900">Política de Fechas</p>
                    <p class="text-lg font-semibold text-green-800">
                      {{ plan.params?.weekday ? 'Flexible' : 'Estricta' }}
                    </p>
                    <p class="text-xs text-green-700">Manejo de feriados</p>
                  </div>
                </div>
              </div>

              <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div class="flex items-center">
                  <i class="fas fa-shield-alt text-yellow-600 mr-2"></i>
                  <div>
                    <p class="text-sm font-medium text-yellow-900">Nivel de Respaldo</p>
                    <p class="text-lg font-semibold text-yellow-800">
                      {{ plan.params?.generate_pagares && plan.params?.generate_pagares_fisico ? 'Alto' :
                         (plan.params?.generate_pagares || plan.params?.generate_pagares_fisico) ? 'Medio' : 'Bajo' }}
                    </p>
                    <p class="text-xs text-yellow-700">Seguridad legal</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Acciones -->
          <div class="mt-6 pt-6 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex space-x-3">
                <!--
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <i class="fas fa-file-download mr-2"></i>
                  Descargar Configuración
                </button>
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <i class="fas fa-copy mr-2"></i>
                  Copiar Configuración
                </button>
                -->
                <button class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <i class="fas fa-history mr-2"></i>
                  Ver Historial
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Sidebar: Contextual Help - Solo visible en pantallas XL -->
      <div class="hidden xl:block xl:col-span-3">
        <div class="bg-white rounded-lg shadow-sm border sticky top-8">
          <div class="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center">
              <i class="fas fa-lightbulb text-yellow-500 mr-2"></i>
              Ayuda Contextual
            </h3>
            <p class="text-xs text-gray-600 mt-1">Información sobre la sección actual</p>
          </div>

          <div class="p-4">
            <!-- Ayuda para Resumen -->
            <div *ngIf="activeSection === 'resumen'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-blue-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-blue-800">Vista de Resumen del Plan</h4>
                  <div class="text-sm space-y-2 text-blue-800">
                    <p><strong>Propósito:</strong> Información consolidada de todas las características y configuraciones del plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Información Incluida:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Datos Básicos:</strong> Nombre, código, tipo y estado del plan</li>
                        <li><strong>Configuración Financiera:</strong> Montos, cuotas y términos de crédito</li>
                        <li><strong>Productos:</strong> Identificadores y tipos de productos asociados</li>
                        <li><strong>Filtros:</strong> Etiquetas y restricciones de acceso</li>
                        <li><strong>Métricas:</strong> Estadísticas actuales de uso del plan</li>
                        <li><strong>Configuración Adicional:</strong> Pagarés y políticas especiales</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Beneficios del Resumen:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Vista Completa:</strong> Toda la información en una sola pantalla</li>
                        <li><strong>Formato Minimalista:</strong> Datos organizados de manera clara y concisa</li>
                        <li><strong>Acceso Rápido:</strong> Información clave sin navegar entre secciones</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-blue-100">
                      <p class="font-medium text-xs">📋 Tip:</p>
                      <p class="text-xs">Usa el resumen para obtener una visión general antes de editar secciones específicas</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Etiquetas -->
            <div *ngIf="activeSection === 'etiquetas'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-blue-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-blue-800">¿Cómo funcionan las Etiquetas?</h4>
                  <div class="text-sm space-y-2 text-blue-800">
                    <p><strong>Propósito:</strong> Las etiquetas permiten segmentar qué clientes pueden acceder a este plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Modos de Filtro:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Lista Blanca:</strong> Solo usuarios CON estas etiquetas pueden acceder</li>
                        <li><strong>Lista Negra:</strong> Solo usuarios SIN estas etiquetas pueden acceder</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Ejemplos de Etiquetas:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><code class="bg-white px-1 rounded text-xs">EMPLEADO_PUBLICO</code> - Funcionarios del sector público</li>
                        <li><code class="bg-white px-1 rounded text-xs">CLIENTE_PREMIUM</code> - Clientes con historial crediticio excelente</li>
                        <li><code class="bg-white px-1 rounded text-xs">MOROSO</code> - Clientes con pagos en mora</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-blue-100">
                      <p class="font-medium text-xs">💡 Tip:</p>
                      <p class="text-xs">Sin etiquetas = Todos los usuarios pueden acceder</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Penalidades -->
            <div *ngIf="activeSection === 'penalidades'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-indigo-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-indigo-800">¿Cómo funcionan las Penalidades?</h4>
                  <div class="text-sm space-y-3 text-indigo-800">
                    <p><strong>Propósito:</strong> Las penalidades (fees) son cargos adicionales que se aplican al plan de crédito.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-2">Tipos de Listas:</p>
                      <ul class="list-disc list-inside space-y-2 ml-2 text-xs">
                        <li><strong>Lista Blanca:</strong> Solo estas penalidades específicas se pueden aplicar al plan</li>
                        <li><strong>Lista Negra:</strong> Estas penalidades NO se pueden aplicar al plan (están excluidas)</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-3 rounded bg-indigo-100 border border-indigo-200">
                      <p class="font-medium text-sm mb-2">🔍 Reglas de Aplicación:</p>
                      <div class="space-y-2 text-xs">
                        <div class="p-2 bg-white rounded border border-green-200">
                          <p class="font-medium text-green-700 mb-1">✓ Penalidad ACTIVA en el producto:</p>
                          <p>Aplica a <strong>todos los planes</strong> automáticamente, excepto si se agrega a la <strong>Lista Negra</strong> del plan.</p>
                        </div>
                        <div class="p-2 bg-white rounded border border-orange-200">
                          <p class="font-medium text-orange-700 mb-1">⊗ Penalidad INACTIVA en el producto:</p>
                          <p>Solo aplica si se agrega explícitamente a la <strong>Lista Blanca</strong> del plan.</p>
                        </div>
                      </div>
                    </div>

                    <div class="mt-3 p-2 rounded bg-indigo-100">
                      <p class="font-medium text-xs">💡 Tip:</p>
                      <p class="text-xs">Si un plan no tiene listas configuradas, se aplican todas las penalidades activas del sistema</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Filtros -->
            <div *ngIf="activeSection === 'filtros'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-green-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-green-800">¿Cómo funcionan los Filtros?</h4>
                  <div class="text-sm space-y-2 text-green-800">
                    <p><strong>Propósito:</strong> Los filtros controlan qué usuarios y en qué tiendas está disponible este plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Tipos de Filtros:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Persona:</strong> Filtra por tipo de cliente (Natural/Jurídica)</li>
                        <li><strong>Tiendas:</strong> Controla en qué puntos de venta está disponible</li>
                        <li><strong>Códigos Dinámicos:</strong> Filtros avanzados por etiquetas específicas</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-green-100">
                      <p class="font-medium text-xs">💡 Tip:</p>
                      <p class="text-xs">Combina múltiples filtros para un control granular de elegibilidad</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Productos -->
            <div *ngIf="activeSection === 'productos'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-purple-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-purple-800">¿Cómo funcionan los Productos?</h4>
                  <div class="text-sm space-y-2 text-purple-800">
                    <p><strong>Propósito:</strong> Los identificadores determinan a qué productos se aplica este plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Tipos Disponibles:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><code class="bg-white px-1 rounded text-xs">CREDIT</code> - Productos de crédito básico</li>
                        <li><code class="bg-white px-1 rounded text-xs">RECHARGE</code> - Productos de recarga</li>
                        <li><code class="bg-white px-1 rounded text-xs">SEGUROS_MERCANTIL</code> - Seguros del Banco Mercantil</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Funcionamiento:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Restricción:</strong> Solo se puede asignar UN identificador por plan</li>
                        <li><strong>Herencia:</strong> El plan hereda las características del producto</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-purple-100">
                      <p class="font-medium text-xs">💡 Tip:</p>
                      <p class="text-xs">Solo se puede asignar UN identificador por plan</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Datos Básicos -->
            <div *ngIf="activeSection === 'datos-basicos'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-orange-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-orange-800">¿Qué son los Datos Básicos?</h4>
                  <div class="text-sm space-y-2 text-orange-800">
                    <p><strong>Propósito:</strong> Define la identidad y configuración fundamental del plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Campos Principales:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Nombre:</strong> Identificación amigable del plan (ej: "Plan Empleados 2024")</li>
                        <li><strong>Código:</strong> Identificador único inmutable (ej: "EMP_2024_V1")</li>
                        <li><strong>Descripción:</strong> Detalles del plan para clientes</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Estados del Plan:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><span class="text-green-600">●</span> <strong>Activo:</strong> Visible y disponible para clientes</li>
                        <li><span class="text-red-600">●</span> <strong>Inactivo:</strong> Oculto para clientes</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-orange-100">
                      <p class="font-medium text-xs">⚠️ Importante:</p>
                      <p class="text-xs">El código NO se puede cambiar una vez creado</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Datos Generales -->
            <div *ngIf="activeSection === 'datos-generales'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-red-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-red-800">¿Cómo configurar los Datos Generales?</h4>
                  <div class="text-sm space-y-2 text-red-800">
                    <p><strong>Propósito:</strong> Configuración financiera y términos de crédito del plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Pago Inicial:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Requiere inicial:</strong> Si el plan exige pago inicial</li>
                        <li><strong>Tipo de cálculo:</strong> Porcentaje o monto fijo (ej: 20% o $50)</li>
                        <li><strong>Recalcular cuotas:</strong> Si ajusta cuotas tras el inicial</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Plazos y Cuotas:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Opciones de cuotas:</strong> Cantidad de cuotas disponibles (ej: 2, 3, 4 cuotas)</li>
                        <li><strong>Frecuencia:</strong> Periodicidad de pago (ej: Mensual, quincenal)</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 bg-white rounded border">
                      <p class="font-medium text-xs mb-1">Ejemplo Plan Empleados:</p>
                      <ul class="text-xs space-y-1">
                        <li>• Inicial: 20% del monto</li>
                        <li>• Cuotas: 2, 3, 4 opciones</li>
                        <li>• Cupo: $500 - $2,000</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-red-100">
                      <p class="font-medium text-xs">💡 Tip:</p>
                      <p class="text-xs">Define términos de crédito y límites según el perfil de usuario</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Clientes -->
            <div *ngIf="activeSection === 'clientes'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-indigo-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-indigo-800">¿Cómo interpretar los Clientes?</h4>
                  <div class="text-sm space-y-2 text-indigo-800">
                    <p><strong>Propósito:</strong> Usuarios que actualmente tienen créditos activos con este plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Estados de Cliente:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><span class="text-green-600">●</span> <strong>Al día:</strong> Pagos al corriente</li>
                        <li><span class="text-red-600">●</span> <strong>Atrasado:</strong> Pagos en mora</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Información Mostrada:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Crédito activo:</strong> Monto actual del crédito</li>
                        <li><strong>Progreso:</strong> Cuotas pagadas vs total</li>
                        <li><strong>Fecha inicio:</strong> Cuándo se otorgó el crédito</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-indigo-100">
                      <p class="font-medium text-xs">📊 Análisis:</p>
                      <p class="text-xs">Evalúa el rendimiento y riesgo del plan</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Historial -->
            <div *ngIf="activeSection === 'historial'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-gray-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-gray-800">¿Para qué sirve el Historial?</h4>
                  <div class="text-sm space-y-2 text-gray-800">
                    <p><strong>Propósito:</strong> Registro completo de cambios y modificaciones del plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Tipos de Cambios:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Modificaciones:</strong> Cambios en configuración</li>
                        <li><strong>Versiones:</strong> Nuevas versiones del plan</li>
                        <li><strong>Estados:</strong> Activación/desactivación</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Información de Auditoría:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Fecha y hora:</strong> Cuándo se realizó el cambio</li>
                        <li><strong>Usuario:</strong> Quién realizó la modificación</li>
                        <li><strong>Detalles:</strong> Qué campos se modificaron</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-gray-100">
                      <p class="font-medium text-xs">🔍 Auditoría:</p>
                      <p class="text-xs">Esencial para cumplimiento y trazabilidad</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Métricas -->
            <div *ngIf="activeSection === 'metricas'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-teal-800">¿Cómo interpretar las Métricas?</h4>
                  <div class="text-sm space-y-2 text-teal-800">
                    <p><strong>Propósito:</strong> Indicadores de rendimiento y estadísticas del plan.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Indicadores Clave:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><span class="text-green-600">●</span> <strong>Tasa de aprobación:</strong> Porcentaje de solicitudes aprobadas</li>
                        <li><strong>Promedio por crédito:</strong> Monto promedio otorgado</li>
                        <li><strong>Ingresos del mes:</strong> Ingresos generados por el plan</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Interpretación:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><span class="text-green-600">●</span> <strong>Verde:</strong> Indicadores positivos</li>
                        <li><span class="text-red-600">●</span> <strong>Rojo:</strong> Requiere atención</li>
                        <li><strong>Tendencias:</strong> Observa cambios en el tiempo</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-teal-100">
                      <p class="font-medium text-xs">📈 Optimización:</p>
                      <p class="text-xs">Usa estas métricas para mejorar el rendimiento del plan</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ayuda para Otros -->
            <div *ngIf="activeSection === 'otros'" class="space-y-4">
              <div class="flex items-start">
                <i class="fas fa-info-circle text-yellow-600 mt-1 mr-3 flex-shrink-0"></i>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold mb-2 text-yellow-800">¿Qué configurar en Otros?</h4>
                  <div class="text-sm space-y-2 text-yellow-800">
                    <p><strong>Propósito:</strong> Configuración adicional de documentación legal y políticas de cobranza.</p>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Documentación Legal:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Pagaré Electrónico:</strong> Genera automáticamente documento digital</li>
                        <li><strong>Pagaré Físico:</strong> Requiere documento físico firmado</li>
                      </ul>
                    </div>

                    <div class="mt-3">
                      <p class="font-medium mb-1">Políticas de Cobranza:</p>
                      <ul class="list-disc list-inside space-y-1 ml-2 text-xs">
                        <li><strong>Permitir cuotas en feriados:</strong> Si las fechas de pago pueden caer en fines de semana o feriados</li>
                        <li><strong>Ajuste automático:</strong> Mueve fechas al siguiente día hábil si está desactivado</li>
                        <li><strong>Diferir cuotas en feriados:</strong> Mueve automáticamente las cuotas de fines de semana y feriados al siguiente día hábil</li>
                        <li><strong>Permitir en cualquier día:</strong> Las fechas pueden caer en cualquier día si está desactivado</li>
                      </ul>
                    </div>

                    <div class="mt-3 p-2 rounded bg-yellow-100">
                      <p class="font-medium text-xs">⚖️ Legal:</p>
                      <p class="text-xs">Los pagarés son documentos legales que respaldan el crédito</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mensaje por defecto cuando no hay sección activa -->
            <div *ngIf="!activeSection" class="text-center py-8">
              <i class="fas fa-compass text-gray-400 text-3xl mb-3"></i>
              <p class="text-sm text-gray-500">Selecciona una sección para ver la ayuda contextual</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Plan Not Found -->
<div *ngIf="!planLoading && !plan" class="min-h-screen flex items-center justify-center bg-gray-50">
  <div class="text-center">
    <i class="fas fa-exclamation-triangle text-yellow-500 text-6xl mb-4"></i>
    <h2 class="text-2xl font-bold text-gray-900 mb-2">Plan no encontrado</h2>
    <p class="text-gray-600 mb-6">El plan solicitado no existe o no tienes permisos para verlo.</p>
    <button (click)="goBack()"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
      <i class="fas fa-arrow-left mr-2"></i>
      Volver a Dashboard
    </button>
  </div>
</div>
